import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableOptions } from "../../../../shared-kernel/components/Table";
import {
  createAdapterToFieldInvestmentMovementTypeEndpoint,
  createAdapterToInvestmentMovementType,
  createAdapterToInvestmentMovementTypeEndpoint
} from "../adapters/investmentMovementType.adapter";
import { EndpointInvestmentMovementTypeFiltered, InvestmentMovementType, InvestmentMovementTypeEndpoint } from "../domain/InvestmentMovementType.model";
import { appConfig } from "../../../../config";
import { EndpointInvestmentMovementType } from "../domain/InvestmentMovementType.model";
import { createAdapterToFilterBackend } from "../../../../utilities/paramsAdapter";

const BASE_INVESTMENT_MOVEMENT_TYPE_URL = '/entidadesfinancieras/tiposmovimientosinversiones'
const BASE_INVESTMENT_MOVEMENT_TYPE_MUTATION_URL = '/entidadesfinancieras/tipomovimientoinversion'
export const BASE_EXPORT_URL = `${appConfig.API_NODE_URL}/entidadesfinancieras/tiposmovimientosinversiones/export`;

export const investmentMovementTypeApi = createApi({
  reducerPath: "investmentMovementTypeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: appConfig.API_NODE_URL,
    prepareHeaders: (headers: Headers) => {
      const APP_TOKEN = "app_token";
      const token = localStorage.getItem(APP_TOKEN);

      if (token) {
        headers.set("authorization", `${token}`);
      }

      return headers;
    },
    credentials: "include",
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: ["InvestmentMovementsTypes", "InvestmentMovementsType"],
  endpoints: (builder) => ({
    getInvestmentMovementType: builder.query<InvestmentMovementType, number | undefined>({
      query: (id) => `${BASE_INVESTMENT_MOVEMENT_TYPE_MUTATION_URL}/${id}`,
      transformResponse: (response: EndpointInvestmentMovementType) => {
        return createAdapterToInvestmentMovementType(response.Content)
      },
      providesTags: (_res, _err, id) => [{type: "InvestmentMovementsType", id: id?.toString()}],
    }),
    getInvestmentMovementsTypes: builder.query<any, TableOptions<InvestmentMovementType>|undefined>({
      query: (params: TableOptions<InvestmentMovementType>) => {
        const { page, pageSize, order, search, filters } = params || {};
        const adaptersFilter = filters?.map( f => createAdapterToFilterBackend(f,createAdapterToFieldInvestmentMovementTypeEndpoint))
        const serializedFilters = adaptersFilter ? JSON.stringify(adaptersFilter) : undefined;
        const serializedOrder = order ? JSON.stringify({field:createAdapterToFieldInvestmentMovementTypeEndpoint(order.field), order:order.order}) : undefined;
        return {
          url:`${BASE_INVESTMENT_MOVEMENT_TYPE_URL}/filtros`,
          params: {
            page:page? page - 1: 0,
            pageSize,
            order: serializedOrder,
            search,
            filters:serializedFilters
          },
        }
      },
      providesTags: ["InvestmentMovementsTypes"],
      transformResponse: (response: EndpointInvestmentMovementTypeFiltered, _meta, params: TableOptions<InvestmentMovementType>) => {
        const page = params?.page ?? 1;
        const endpointResponse = response?.rows?.map((investmentMovementType: InvestmentMovementTypeEndpoint) =>
            createAdapterToInvestmentMovementType(investmentMovementType)
        );
        return {
          data: endpointResponse,
          page,
          total: response.total,
        };
      },
    }),
    createInvestmentMovementType: builder.mutation<any, InvestmentMovementType>({
      query: (data) => ({
        url: `${BASE_INVESTMENT_MOVEMENT_TYPE_MUTATION_URL}`,
        method: "POST",
        body: {
          "Content": createAdapterToInvestmentMovementTypeEndpoint(data)
        },
      }),
      invalidatesTags: ["InvestmentMovementsTypes"],
    }),
    updateInvestmentMovementType: builder.mutation<any, InvestmentMovementType>({
      query: (data: InvestmentMovementType) => ({
        url: `${BASE_INVESTMENT_MOVEMENT_TYPE_MUTATION_URL}`,
        method: "PUT",
        body: {
          "Content": createAdapterToInvestmentMovementTypeEndpoint(data)
        },
      }),
      invalidatesTags: (_res, _err, data: InvestmentMovementType) => ["InvestmentMovementsTypes", {type: 'InvestmentMovementsType', id: data?.id?.toString()}],
    }),
    deleteInvestmentMovementType: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE_INVESTMENT_MOVEMENT_TYPE_MUTATION_URL}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InvestmentMovementsTypes"],
    }),
  }),
});

export const {
  useGetInvestmentMovementTypeQuery,
  useGetInvestmentMovementsTypesQuery,
  useCreateInvestmentMovementTypeMutation,
  useUpdateInvestmentMovementTypeMutation,
  useDeleteInvestmentMovementTypeMutation
} = investmentMovementTypeApi;
